import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatPanel } from './components/ChatPanel';
import { HandshakeError, postSession, type AgentMeta, type IdentitySignature } from './api';
import {
  decodeInbound,
  isFromParent,
  postToParent,
  type EndUserInfo,
  type ParentInbound,
  type ToolDefinition,
} from './parent-bridge';
import { mergeStrings, type EmbedStrings } from './strings';

type HandshakeContext = {
  parentOrigin: string;
  tools: ToolDefinition[];
  endUser?: EndUserInfo;
  customerMetadata?: Record<string, string>;
  identitySignature?: IdentitySignature;
};

const KEY_RE = /^pk_live_[A-Za-z0-9_-]{20,}$/;

type Status =
  | { kind: 'waiting-init' }
  | { kind: 'handshaking'; parentOrigin: string; tools: ToolDefinition[] }
  | {
      kind: 'ready';
      parentOrigin: string;
      sessionToken: string;
      agent: AgentMeta;
      tools: ToolDefinition[];
    }
  | { kind: 'error'; reason: string };

export function App() {
  const [status, setStatus] = useState<Status>({ kind: 'waiting-init' });
  // String overrides forwarded by the loader at init time. Stored at App
  // level (not deeper) because the loading/error state screens also need
  // them — not just ChatPanel. Default merge happens lazily below.
  const [stringOverrides, setStringOverrides] = useState<Partial<EmbedStrings> | undefined>(
    undefined,
  );
  const strings = useMemo(() => mergeStrings(stringOverrides), [stringOverrides]);
  // Latest live context pushed by the host loader via 'agents/live-context-update'.
  // Read per-chat-send via a ref (so updates don't re-render ChatPanel — the
  // transport reads on demand). Stored as null when host clears it; the
  // transport treats null/empty as "don't include the field".
  const liveContextRef = useRef<string | null>(null);
  // We commit to one parent origin on first init — later inbound messages
  // from a different origin are dropped (anti-postMessage spoof). NOTE: in
  // a sandboxed iframe (origin "null") this gate is effectively a no-op
  // and `e.source === window.parent` is the real check. See parent-bridge.ts.
  const lockedParentOriginRef = useRef<string | null>(null);
  // Captured on first handshake so we can re-handshake transparently when the
  // session JWT expires mid-conversation — without forcing the user to reload.
  const handshakeContextRef = useRef<HandshakeContext | null>(null);

  // --- agentKey + optional theme from query string (set by the loader's
  // iframe URL, or hand-crafted by the admin preview which doesn't go through
  // the postMessage init path).
  const agentKey = (() => {
    try {
      return new URL(window.location.href).searchParams.get('agentKey') ?? '';
    } catch {
      return '';
    }
  })();

  // Apply URL-supplied theme on first paint so the loading screen already
  // matches the customer's palette. Init can later override (loader has the
  // final say once it sends `agents/init`). Tight regex keeps it
  // safe to splat into a CSS attribute selector / data-attribute.
  useEffect(() => {
    try {
      const t = new URL(window.location.href).searchParams.get('theme') ?? '';
      if (t && /^[a-z][a-z0-9-]{0,31}$/.test(t)) {
        document.documentElement.dataset.theme = t;
      }
    } catch {
      /* malformed URL — leave default */
    }
  }, []);

  // --- bridge wiring ----------------------------------------------------
  useEffect(() => {
    if (!KEY_RE.test(agentKey)) {
      setStatus({ kind: 'error', reason: 'invalid_agent_key' });
      return;
    }

    function onMessage(e: MessageEvent) {
      if (!isFromParent(e)) return;
      const msg = decodeInbound(e.data);
      if (!msg) return;

      if (lockedParentOriginRef.current === null) {
        // Pre-init: only agents/init is acceptable; pin its origin.
        if (msg.type !== 'agents/init') return;
        lockedParentOriginRef.current = e.origin;
        startHandshake(msg, e.origin);
        return;
      }

      // Post-init: enforce same-origin pinning.
      if (e.origin !== lockedParentOriginRef.current) return;
      handlePostInit(msg);
    }

    function startHandshake(msg: ParentInbound, eventOrigin: string) {
      if (msg.type !== 'agents/init') return;
      // The payload's hostOrigin should match the message's actual origin —
      // they're independent assertions and a mismatch is suspicious. NOTE:
      // in a sandboxed iframe `eventOrigin === "null"`, so the only useful
      // case for this check is detecting a clearly malformed init payload.
      if (msg.payload.hostOrigin !== eventOrigin) {
        setStatus({ kind: 'error', reason: 'origin_mismatch' });
        return;
      }
      // Apply string overrides BEFORE moving to handshaking state so the
      // "Carregando…" indicator already uses the customer's locale.
      if (msg.payload.strings) setStringOverrides(msg.payload.strings);
      if (msg.payload.theme) document.documentElement.dataset.theme = msg.payload.theme;
      const tools = msg.payload.tools ?? [];
      setStatus({ kind: 'handshaking', parentOrigin: eventOrigin, tools });
      void doHandshake(
        eventOrigin,
        tools,
        msg.payload.endUser,
        msg.payload.customerMetadata,
        msg.payload.identitySignature,
      );
    }

    function handlePostInit(msg: ParentInbound) {
      // open/close are advisory — the iframe always renders the chat. The
      // loader controls visibility on the parent side. We could pause/resume
      // streams here, but MVP doesn't need it.
      if (msg.type === 'agents/live-context-update') {
        // Empty string clears (host signals "no context"); any other value
        // becomes the latest. Decoder already capped at 4096 chars.
        liveContextRef.current = msg.payload.value.length > 0 ? msg.payload.value : null;
      }
    }

    async function doHandshake(
      parentOrigin: string,
      tools: ToolDefinition[],
      endUser?: EndUserInfo,
      customerMetadata?: Record<string, string>,
      identitySignature?: IdentitySignature,
    ) {
      handshakeContextRef.current = {
        parentOrigin,
        tools,
        endUser,
        customerMetadata,
        identitySignature,
      };
      try {
        // Tools são declaradas pelo parent (loader push) e enviadas inline
        // em cada POST /widget/chat — handshake só troca pk_live_* por JWT.
        const sess = await postSession({
          publicKey: agentKey,
          parentOrigin,
          endUser,
          customerMetadata,
          identitySignature,
        });
        setStatus({
          kind: 'ready',
          parentOrigin,
          sessionToken: sess.sessionToken,
          agent: sess.agent,
          tools,
        });
      } catch (err) {
        const reason =
          err instanceof HandshakeError ? err.code : 'unknown';
        setStatus({ kind: 'error', reason });
      }
    }

    window.addEventListener('message', onMessage);
    // Tell parent we're alive. targetOrigin '*' acceptable here — `ready`
    // carries no secret.
    postToParent({ type: 'agents/ready' }, '*');

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [agentKey]);

  // Re-handshake when the widget chat hits a 401 mid-conversation. The widget
  // JWT has a 15min TTL and there's no refresh-token flow on the backend, so
  // we just transparently swap pk_live_* for a fresh JWT and let ChatPanel
  // pick it up via the new `session.token`. If re-handshake fails we fall
  // back to the visible error state ("Sessão expirou. Recarregue a página.").
  const reHandshake = useCallback(async () => {
    const ctx = handshakeContextRef.current;
    if (!ctx) {
      setStatus({ kind: 'error', reason: 'session_expired' });
      return;
    }
    try {
      const sess = await postSession({
        publicKey: agentKey,
        parentOrigin: ctx.parentOrigin,
        endUser: ctx.endUser,
        customerMetadata: ctx.customerMetadata,
        identitySignature: ctx.identitySignature,
      });
      setStatus({
        kind: 'ready',
        parentOrigin: ctx.parentOrigin,
        sessionToken: sess.sessionToken,
        agent: sess.agent,
        tools: ctx.tools,
      });
    } catch {
      setStatus({ kind: 'error', reason: 'session_expired' });
    }
  }, [agentKey]);

  // Loading and error states need their own close button — without it the user
  // is stuck staring at the iframe with no way out (the ChatPanel's header X
  // only mounts after a successful handshake). targetOrigin '*' is fine here:
  // the close-request payload is non-secret and we may not yet have a pinned
  // parentOrigin (handshake state) — postMessage still delivers only to the
  // exact parent window reference.
  const requestClose = () =>
    postToParent({ type: 'agents/close-request' }, '*');

  // --- render ----------------------------------------------------------
  if (status.kind === 'error') {
    return (
      <div className="kb-state kb-state--error" role="alert">
        <CloseButton ariaLabel={strings.closeLabel} onClick={requestClose} />
        <p>{strings.errorLoadingChat}</p>
        <p className="kb-state__detail">{labelForReason(status.reason, strings)}</p>
      </div>
    );
  }

  if (status.kind === 'waiting-init' || status.kind === 'handshaking') {
    return (
      <div className="kb-state kb-state--loading" role="status">
        <CloseButton ariaLabel={strings.closeLabel} onClick={requestClose} />
        <span>{strings.loading}</span>
      </div>
    );
  }

  return (
    <ChatPanel
      session={{ token: status.sessionToken, agent: status.agent }}
      tools={status.tools}
      parentOrigin={status.parentOrigin}
      strings={strings}
      onCloseRequest={() => postToParent({ type: 'agents/close-request' }, status.parentOrigin)}
      onSessionExpired={() => void reHandshake()}
      getLiveContext={() => liveContextRef.current}
    />
  );
}

function CloseButton({ ariaLabel, onClick }: { ariaLabel: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="kb-state__close"
      aria-label={ariaLabel}
      onClick={onClick}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M3 3l10 10M13 3L3 13" />
      </svg>
    </button>
  );
}

function labelForReason(reason: string, strings: EmbedStrings): string {
  switch (reason) {
    case 'invalid_agent_key':
      return strings.reasonInvalidAgentKey;
    case 'invalid_key':
      return strings.reasonInvalidKey;
    case 'origin_not_allowed':
      return strings.reasonOriginNotAllowed;
    case 'origin_required':
      return strings.reasonOriginRequired;
    case 'origin_mismatch':
      return strings.reasonOriginMismatch;
    case 'rate_limited':
      return strings.reasonRateLimited;
    case 'agent_not_found':
      return strings.reasonAgentNotFound;
    case 'session_expired':
      return strings.reasonSessionExpired;
    case 'NETWORK':
      return strings.reasonNetwork;
    default:
      return strings.reasonUnknown;
  }
}
